import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Moment from "moment";
import { extendMoment } from "moment-range";
import { Space, Spin, Radio } from "antd";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import ACTIONS from "../../store/action";
import { selectCalendarViewType, selectFilters, selectViewType } from "../../store/selector";
import { PRIMARY_675 } from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";
import { CALENDAR_MONTH_VIEW, CALENDAR_WEEK_VIEW } from "../../constant/viewTypes";
import MonthlyCalendarView from "./MonthlyCalendarView";
import { WeeklyCalendarView } from "./WeeklyCalendarView";
import { ALL, NEXT_MONTH, NEXT_WEEK, THIS_MONTH, THIS_WEEK, LAST_WEEK, LAST_MONTH } from "../../constant/filters";
import { FilterListForm } from "../SavedList/FilterListForm";
import { DATE_FORMAT_2 } from "../../utils/date";
import { ViewType } from "../../constant/general";
import { OrderFilters } from "../OrdersFilter";
import { Routes } from "../../constant/routes";

const moment = extendMoment(Moment);

// Set locale to start the week on Monday
moment.updateLocale("en", {
  week: {
    dow: 1 // Monday is the first day of the week
  }
});

const StyledRadioGroup = styled(Radio.Group)`
  padding-left: ${(props) => (props.filtersVisible ? "30px" : "30px")};
  margin-left: ${(props) => (props.filtersVisible ? "0px" : "120px")};
  &.ant-radio-group-solid .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: ${PRIMARY_675};
    min-width: 75px;
  }

  .ant-radio-button-wrapper {
    border: 1px solid ${PRIMARY_675};
    border-color: ${PRIMARY_675};
    border-right-color: ${PRIMARY_675};
    min-width: 75px;
  }

  .ant-radio-button-wrapper:not(:first-child)::before {
    background-color: ${PRIMARY_675};
  }

  .ant-radio-button-wrapper {
    color: ${PRIMARY_675};
  }

  label > span {
    display: flex;
    justify-content: center;
    font-weight: ${fontWeights.semibold};
    font-size: ${sizes.medium};
  }
`;

const ContainerStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-top: ${(props) => (props.filterListId ? "0px" : "20px")};
  transition: all 0.3s ease;
`;

const OrdersCalendar = ({ patients, isFetching, onClickCalendar, onCalenderViewChange }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { savedListViewType, filterListId } = useParams();
  const formatDate = (date, format) => (date ? moment(date, DATE_FORMAT_2).format(format) : "");
  const setCalendarOrderDateFilterACT = (calendarOrderDateFilter) =>
    dispatch(ACTIONS.setCalendarOrderDateFilter(calendarOrderDateFilter));

  const calendarViewType = useSelector(selectCalendarViewType);
  const viewType = useSelector(selectViewType);
  const filters = useSelector(selectFilters);
  const { orderDateFilter } = filters;

  const [firstDayOfWeek, setFirstDayOfWeek] = useState(null);

  const [firstDayOfMonth, setFirstDayOfMonth] = useState(null);
  const [startDayOfMonthlyCalendar, setStartDayOfMonthlyCalendar] = useState(null);
  const [lastDayOfMonthlyCalendar, setLastDayOfMonthlyCalendar] = useState(null);

  const [weekOrdersGroupByDays, setWeekOrdersGroupByDays] = useState([]);
  const [monthOrdersGroupByDays, setMonthOrdersGroupByDays] = useState([]);
  const [filtersVisible, setFiltersVisible] = useState(false);

  const setFormPopupsAct = (visible) => dispatch(ACTIONS.setFormPopups(visible));
  const isSavedListView = location.pathname.includes(Routes.SAVEDLISTS) ? true : false;

  const lastDayOfWeek = moment(firstDayOfWeek).add(6, "days").endOf("day");

  const setCalendarToViewMode = (startOfWeek, startOfMonth, startDate) => {
    startDate ? setFirstDayOfMonth(startDate.clone().startOf("month")) : setFirstDayOfMonth(moment().startOf("month"));
    setStartDayOfMonthlyCalendar(startOfMonth);
    startDate
      ? setLastDayOfMonthlyCalendar(moment(startDate?.clone()).endOf("month").endOf("week"))
      : setLastDayOfMonthlyCalendar(moment().endOf("month").endOf("week"));
    setFirstDayOfWeek(startOfWeek);
  };

  useEffect(() => {
    setStartDayOfMonthlyCalendar(moment(firstDayOfMonth?.clone()).startOf("month").startOf("week"));
    setLastDayOfMonthlyCalendar(moment(firstDayOfMonth?.clone()).endOf("month").endOf("week"));
    setCalendarOrderDateFilterACT({
      fromDate: moment(firstDayOfMonth?.clone()).startOf("month").startOf("week"),
      toDate: moment(firstDayOfMonth?.clone()).endOf("month").endOf("week")
    });
  }, [firstDayOfMonth]);

  useEffect(() => {
    !isFetching && updateCalendarOrders(calendarViewType);
  }, [startDayOfMonthlyCalendar, firstDayOfWeek]);

  const getCalendarViewType = () => {
    const { fromDate, toDate } = orderDateFilter;
    if (fromDate && toDate) {
      const startDate = moment(fromDate).local();
      const endDate = moment(toDate);
      const days = Math.ceil(
        moment.duration(endDate.clone().endOf("day").diff(startDate.clone().startOf("day"))).asDays()
      );
      if (days >= 30) {
        const startOfMonth = startDate.clone().startOf("month").startOf("week");
        const startOfWeek = startDate.isoWeekday() === 1 ? startDate : startDate.clone().startOf("week");
        setCalendarToViewMode(startOfWeek, startOfMonth, startDate);
      } else if (days < 30) {
        const startOfWeek = startDate.isoWeekday() === 1 ? startDate.clone() : startDate.clone().startOf("isoweek");
        const startOfMonth = startOfWeek.clone().startOf("month").startOf("week");
        setCalendarToViewMode(startOfWeek, startOfMonth, startDate);
      }
    } else if (fromDate === THIS_MONTH) {
      const startOfMonth = moment().startOf("month").startOf("week");
      const startOfWeek = startOfMonth.clone().startOf("week");
      setCalendarToViewMode(startOfWeek, startOfMonth);
    } else if (fromDate === THIS_WEEK) {
      const startOfWeek = moment().startOf("week");
      const startOfMonth = startOfWeek.clone().startOf("month").startOf("week");
      setCalendarToViewMode(startOfWeek, startOfMonth);
    } else if (fromDate === ALL) {
      const startOfMonth = moment().startOf("month").startOf("week");
      const startOfWeek = moment().startOf("week");
      setCalendarToViewMode(startOfWeek, startOfMonth);
    } else if (fromDate === LAST_MONTH) {
      const startDate = moment().subtract(1, "months").startOf("day");
      const startOfMonth = startDate.clone().startOf("month").startOf("week");
      const startOfWeek = startOfMonth.isoWeekday() === 1 ? startOfMonth : startOfMonth.clone().startOf("week");
      setCalendarToViewMode(startOfWeek, startOfMonth, startDate);
    } else if (fromDate === LAST_WEEK) {
      const startDate = moment().subtract(1, "weeks").startOf("day");
      const startOfWeek = startDate.isoWeekday() === 1 ? startDate.clone() : startDate.clone().startOf("isoweek");
      const startOfMonth = startOfWeek.clone().startOf("month").startOf("week");
      setCalendarToViewMode(startOfWeek, startOfMonth, startDate);
    } else if (fromDate === NEXT_MONTH) {
      const startDate = moment().add(1, "months").startOf("day");
      const startOfMonth = startDate.clone().startOf("month").startOf("week");
      const startOfWeek = startOfMonth.isoWeekday() === 1 ? startOfMonth : startOfMonth.clone().startOf("week");
      setCalendarToViewMode(startOfWeek, startOfMonth, startDate);
    } else if (fromDate === NEXT_WEEK) {
      const startDate = moment().add(1, "weeks").startOf("day");
      const startOfWeek = startDate.isoWeekday() === 1 ? startDate.clone() : startDate.clone().startOf("isoweek");
      const startOfMonth = startOfWeek.clone().startOf("month").startOf("week");
      setCalendarToViewMode(startOfWeek, startOfMonth, startDate);
    }
  };

  useEffect(() => {
    getCalendarViewType();
    if (monthOrdersGroupByDays.length === 0 && weekOrdersGroupByDays.length === 0) {
      return;
    }

    setWeekOrdersGroupByDays([]);
    setMonthOrdersGroupByDays([]);
    updateCalendarOrders(calendarViewType);
  }, [filters]);

  const onCalendarViewDisplayChange = (e) => {
    if (e.target.value !== calendarViewType) {
      dispatch(ACTIONS.setCalendarViewType(e.target.value));
      getCalendarViewType();
    }
  };

  const updateCalendarOrders = (viewType) => {
    viewType === CALENDAR_WEEK_VIEW && firstDayOfWeek
      ? onCalenderViewChange(
          formatDate(firstDayOfWeek.clone(), DATE_FORMAT_2),
          formatDate(lastDayOfWeek.clone(), DATE_FORMAT_2)
        )
      : startDayOfMonthlyCalendar &&
        onCalenderViewChange(
          formatDate(startDayOfMonthlyCalendar.clone(), DATE_FORMAT_2),
          formatDate(lastDayOfMonthlyCalendar?.clone(), DATE_FORMAT_2)
        );
  };
  return (
    <div id="calender-view">
      <ContainerStyled filterListId={filterListId}>
        <OrderFilters
          filtersVisible={filtersVisible}
          setFiltersVisible={setFiltersVisible}
          isListView={viewType === ViewType.List.key || savedListViewType === ViewType.List.key}
          isCalendar={true}
          isSavedListView={isSavedListView}
        />

        <div style={{ padding: filtersVisible ? "0px 15px 0px 28px" : "0px 15px 0px 15px", width: "100%" }}>
          <Space>
            <StyledRadioGroup
              onChange={onCalendarViewDisplayChange}
              value={calendarViewType}
              optionType="button"
              buttonStyle="solid"
              filtersVisible={filtersVisible}
            >
              <Radio.Button id="calendar_view_week" value={CALENDAR_WEEK_VIEW}>
                {t("week")}
              </Radio.Button>
              <Radio.Button id="calendar_view_month" value={CALENDAR_MONTH_VIEW}>
                {t("month")}
              </Radio.Button>
            </StyledRadioGroup>
          </Space>
          {!isFetching ? (
            <>
              {calendarViewType === CALENDAR_WEEK_VIEW && firstDayOfWeek && (
                <WeeklyCalendarView
                  patients={patients}
                  weekOrdersGroupByDays={weekOrdersGroupByDays}
                  setWeekOrdersGroupByDays={setWeekOrdersGroupByDays}
                  firstDayOfWeek={firstDayOfWeek}
                  setFirstDayOfWeek={setFirstDayOfWeek}
                  lastDayOfWeek={lastDayOfWeek}
                  onClickCalendar={onClickCalendar}
                  isFetching={isFetching}
                  filtersVisible={filtersVisible}
                  isSavedListView={isSavedListView}
                />
              )}

              {calendarViewType === CALENDAR_MONTH_VIEW && firstDayOfMonth && (
                <MonthlyCalendarView
                  patients={patients}
                  monthOrdersGroupByDays={monthOrdersGroupByDays}
                  setMonthOrdersGroupByDays={setMonthOrdersGroupByDays}
                  startDayOfMonthlyCalendar={startDayOfMonthlyCalendar}
                  lastDayOfMonthlyCalendar={lastDayOfMonthlyCalendar}
                  onClickCalendar={onClickCalendar}
                  isFetching={isFetching}
                  firstDayOfMonth={firstDayOfMonth}
                  setFirstDayOfMonth={setFirstDayOfMonth}
                  getCalendarViewType={getCalendarViewType}
                  filtersVisible={filtersVisible}
                  isSavedListView={isSavedListView}
                />
              )}
            </>
          ) : (
            <div
              style={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: 60, width: "100%" }}
            >
              <Spin size="large" />
            </div>
          )}
        </div>
        <FilterListForm setFormPopupsAct={setFormPopupsAct} />
      </ContainerStyled>
    </div>
  );
};

export { OrdersCalendar };
